import React, { useMemo, useEffect, useState } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import Box from "@mui/material/Box";
import { Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { mkConfig, generateCsv, download } from "export-to-csv";
import MDButton from "components/MDButton";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

interface TableProps {
  arrayColumns: [];
  data: [];
  renderDetailPanel?: any; // Nuevo prop opcional para renderizar panel detallado
  height: number;
  noActionsEdit: boolean;
}

export default function TableView({
  arrayColumns,
  data,
  renderDetailPanel, // Nuevo prop para renderizar detalles
  height,
  noActionsEdit,
}: TableProps) {
  const columns = useMemo(() => arrayColumns, []);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const setWindowDimensions = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  useEffect(() => {
    window.addEventListener("resize", setWindowDimensions);
    return () => {
      window.removeEventListener("resize", setWindowDimensions);
    };
  }, []);

  const customTableContainerProps = {
    sx: {
      maxHeight: windowHeight - height + "px",
      minHeight: "200px",
    },
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableGrouping: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    enableRowActions: false, // Desactiva acciones de fila
    muiTableContainerProps: customTableContainerProps,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "options",
        size: 90,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #e0e0e0",
        fontSize: 14,
        pl: 1,
        pr: 1,
        pt: 0,
        pb: 0,
      },
    },
    muiTableHeadProps: {
      sx: {
        padding: "0",
      },
    },
    mrtTheme: (theme) => ({
      baseBackgroundColor: "#ffffff",
    }),
    muiTableHeadCellProps: {
      sx: (theme) => ({
        fontWeight: "normal",
        backgroundColor: "#ffffff",
        fontSize: {
          xs: "10px",
          sm: "11px",
          md: "12px",
          lg: "13px",
          xl: "14px",
        },
        borderRight: theme.palette.mode === "dark" ? "1px solid #e01111" : "1px solid #e0e0e0",
        bgcolor:
          theme.palette.mode === "light" ? "hsla(220, 60%, 99%, 0.6)" : "hsla(220, 0%, 0%, 0.7)",
      }),
    },
    initialState: {
      density: "compact",
      expanded: {}, // Ninguna fila expandida por defecto
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "4px",
          flexWrap: "wrap",
        }}
      >
        <MDButton
          sx={{
            fontWeight: "normal",
            fontSize: {
              xs: "10px",
              sm: "11px",
              md: "12px",
              lg: "12px",
              xl: "13px",
            },
          }}
          onClick={handleExportData}
          startIcon={<FileDownloadIcon />}
        >
          Export All Data
        </MDButton>
      </Box>
    ),
    renderDetailPanel: renderDetailPanel // Agrega la opción de renderizar el detalle si está definido
      ? ({ row }) => renderDetailPanel(row)
      : undefined,
  });

  return <MaterialReactTable table={table} />;
}
